import { login, logout, currentUser } from "@/features/account/services/accountService";
import { goTo, currentRouteIsPublic } from "@/services/routeService";
import { handleError } from "@/services/errorUtility";
import { createCompletion } from "@/services/completion";
import { DateTime } from "luxon";

function getDefaultState() {
    return {
        user: null,
        policies: null,
        isLoggedIn: false,
        isLoaded: false,
        loggingIn: false,
        loadCompletion: createCompletion()
    }
}

const state = getDefaultState();

const getters = {
    personId: (state) => {
        return state.user?.personId;
    },
    hasPolicy: state => policy => {
        return state.policies?.has(policy) ?? false;
    },
    isInGracePeriod: (state) => {
        if (!state.user || !state.user.renewsAt) {
            return false;
        }

        const renewalDate = DateTime.fromISO(state.user.renewsAt);
        const now = DateTime.local();

        return renewalDate < now;
    }
}

const actions = {
    async load({ commit, dispatch }) {
        await dispatch("refreshUser");
        commit("loaded");
        state.loadCompletion.complete();
    },
    async waitForLoad({ state }) {
        await state.loadCompletion();
    },
    async login({ commit, dispatch }, { emailAddress, password }) {
        try {
            commit("loggingIn", true);
            await login(emailAddress, password);
            await dispatch("refreshUser");

            if (!state.user.isFullyAuthenticated) {
                goTo("/two-factor-authentication");
            }
            else {
                goTo("/");
            }
        }
        catch (error) {
            commit("clear", error);
            handleError(error);
        }
        finally {
            commit("loggingIn", false);
        }
    },

    async logout({ commit }) {
        await logout();
        commit("clear");

        if(!currentRouteIsPublic()) {
            goTo("/login");
        }
    },
    async refreshUser({ commit }) {
        const user = await currentUser();
        if(user !== null) {
            commit("user", user);
        }
    },
    setSettings({ commit, dispatch }, settings) {
        commit("settings", settings);
        dispatch("localisation/load", null, { root: true });
    },
};

const mutations = {
    user(state, user) {
        state.user = user;
        state.policies = new Set(user.policies);
        state.isLoggedIn = true;
    },
    settings(state, settings) {
        if(state.user) {
            Object.assign(state.user, settings);
        }
    },
    loaded(state) {
        state.isLoaded = true;
    },
    loggingIn(state, loggingIn) {
        state.loggingIn = loggingIn;
    },
    clear(state) {
        state.user = null;
        state.isLoggedIn = false;
        state.loggingIn = false;
    }
};

export const authentication = {
    namespaced: true,
    state,
    getters,
    actions,
    mutations
}
